// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@awc/App/Store/Reducers";
import Account from "@awc/XDL/Icons/Account";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrentUserIsGuest,
    getIsPunchOutSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Menu, { MappedLink } from "@insite/mobius/Menu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        userName: state.context.session.userLabel,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
        // XNG-Change
        isDrawerOpen: state.components.addressDrawer.isOpen,
        enableWarehousePickup: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // XNG-Change
    setDrawerIsOpen,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    // XNG-Change
    userInfoWrapper?: InjectableCss;
    signInClickable?: ClickablePresentationProps;
    drawer?: DrawerPresentationProps;
    drawerInShell?: DrawerPresentationProps;
    drawerInPunchOut?: DrawerPresentationProps;
    langugaeAndCurrencyWrapper?: InjectableCss;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
            padding: 6px 0;
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 10px;
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            max-width: 150px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
            text-transform: uppercase;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme> &
    HasShellContext;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // XNG-Change
    isDrawerOpen,
    setDrawerIsOpen,
    shellContext,
    id,
    enableWarehousePickup,
    isMobileScreen,
}: Props) => {
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    // XNG-Changes
    let drawerStyles: DrawerPresentationProps | undefined;
    if (shellContext.isInShell && shellContext.isEditing) {
        drawerStyles = styles.drawerInShell;
    } else if (isPunchOutSession) {
        drawerStyles = styles.drawerInPunchOut;
    } else {
        drawerStyles = styles.drawer;
    }

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    if (!myAccountPageLink) {
        return null;
    }

    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    if (fields.icon === "Account") {
        icon = Account;
    }

    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = (myAccountPageLink?.children?.filter(
        pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    ) ?? []) as MappedLink[];

    const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
        return {
            title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
            iconProps: {
                src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
                size: 22,
                css: css`
                    margin-left: 20px;
                    margin-right: -12px;
                `,
            },
            clickableProps: {
                css: css`
                    display: flex;
                    text-transform: uppercase;
                `,
                onClick: () => {
                    title === "Inventory"
                        ? setShowInventory({ showInventory: !showInventory })
                        : setShowPricing({ showPricing: !showPricing });
                },
            },
        };
    };

    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");

        showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
        showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            e.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
                return;
            }

            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
                }
            }
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    // XNG-Change
    const displayDrawer = () => {
        setDrawerIsOpen({ isOpen: !isDrawerOpen });
    };

    const handleCloseDrawer = () => {
        setDrawerIsOpen({ isOpen: false });
    };

    return (
        <>
            <StyledWrapper {...styles.signOutWrapper}>
                {includeAccountMenu && userName && !currentUserIsGuest && (
                    <Menu
                        descriptionId="accountMenu"
                        menuItems={menuItems}
                        displayChangeCustomerLink={displayChangeCustomerLink}
                        maxDepth={1}
                        menuTrigger={
                            <Clickable {...styles.titleClickable} data-test-selector="header_signIn">
                                {showIcon && <Icon {...styles.titleIcon} src={icon} title={userName} />}
                                {showLabel && (
                                    <Typography {...styles.titleTypography} data-test-selector="header_userName">
                                        {userName}
                                    </Typography>
                                )}
                            </Clickable>
                        }
                        cssOverrides={{
                            wrapper: css`
                                flex: 0 1 auto;
                                min-width: 0;
                            `,
                        }}
                        data-test-selector="header_accountMenu"
                    />
                )}
                {!includeAccountMenu && (!userName || (userName && currentUserIsGuest)) && (
                    <>
                        <Clickable
                            {...styles.signInClickable}
                            onClick={onSignInHandler}
                            href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}
                            data-test-selector="header_signIn"
                        >
                            <Typography {...styles.titleTypography}>{signInStatusText}</Typography>
                        </Clickable>
                        <Hidden above="sm" {...styles.signOutLinkWrapper}>
                            {enableWarehousePickup &&
                                createWidgetElement("Common/HeaderShipToAddress", {
                                    fields: {},
                                } as any)}
                        </Hidden>
                    </>
                )}
                {!includeAccountMenu && (
                    <>
                        {userName && !currentUserIsGuest && (
                            <>
                                <Hidden above="sm" {...styles.signOutLinkWrapper}>
                                    <Clickable {...styles.signOutClickable} onClick={displayDrawer}>
                                        {showIcon && <Icon {...styles.titleIcon} src={icon} title={userName} />}
                                    </Clickable>
                                </Hidden>
                                <Hidden above="sm" {...styles.signOutLinkWrapper}>
                                    {enableWarehousePickup &&
                                        createWidgetElement("Common/HeaderShipToAddress", {
                                            fields: {},
                                        } as any)}
                                </Hidden>
                            </>
                        )}
                        <Hidden below="md" {...styles.userInfoWrapper}>
                            {userName && !currentUserIsGuest && (
                                <Clickable
                                    {...styles.titleClickable}
                                    onClick={displayDrawer}
                                    // XNG-Changes
                                    className="header-brandlogo"
                                    aria-label={signInStatusText}
                                >
                                    {showIcon && (
                                        <Icon
                                            {...styles.titleIcon}
                                            src={icon}
                                            title={signInStatusText}
                                            // xng-change
                                            aria-hidden="true"
                                        />
                                    )}
                                    {showLabel && (
                                        <Typography {...styles.titleTypography}>{signInStatusText}</Typography>
                                    )}
                                </Clickable>
                            )}
                            {/* XNG-Changes */}
                            {enableWarehousePickup &&
                                createWidgetElement("Common/HeaderShipToAddress", {
                                    fields: {},
                                } as any)}
                            {userName && !currentUserIsGuest && (
                                <Clickable
                                    {...styles.signOutClickable}
                                    onClick={onSignOutHandler}
                                    data-test-selector="headerSignOutLink"
                                    // XNG-Changes
                                    className="header-brandlogo"
                                >
                                    <Typography {...styles.titleTypography}>{translate("Sign Out")}</Typography>
                                </Clickable>
                            )}
                        </Hidden>
                    </>
                )}
            </StyledWrapper>
            {/* XNG-Change */}
            <Drawer
                {...drawerStyles}
                position="top"
                isOpen={isDrawerOpen}
                headline={isPunchOutSession ? "" : translate("Change Customer")}
                handleClose={handleCloseDrawer}
                data-test-selector="addressDrawer"
            >
                <Zone zoneName="Container" contentId={id} />
                {isMobileScreen && (
                    <>
                        <GridContainer className="change-customer-row-container">
                            <GridItem className="fulfillment-method-grid-item" width={12}>
                                {createWidgetElement("Header/AddressDrawerFulfillmentMethodSelector", {
                                    fields: {},
                                })}
                            </GridItem>
                            <GridItem className="addresses-grid-item" width={12}>
                                {createWidgetElement("Header/AddressDrawerSelectCustomer", {
                                    fields: {},
                                })}
                            </GridItem>
                            <GridItem className="pick-up-address-grid-item" width={12}>
                                {createWidgetElement("Header/AddressDrawerPickUpLocationSelector", {
                                    fields: {},
                                })}
                            </GridItem>
                            <GridItem className="apply-button-grid-item" width={12}>
                                {createWidgetElement("Header/AddressDrawerApplyButton", { fields: {} })}
                            </GridItem>
                        </GridContainer>
                    </>
                )}
            </Drawer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withIsInShell(withTheme(HeaderSignIn)))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            // {
            //     name: fields.icon,
            //     displayName: "SignIn Icon",
            //     editorTemplate: "DropDownField",
            //     // XNG-Change
            //     options: [
            //         {
            //             displayName: "Account",
            //             value: "Account",
            //         },
            //     ],
            //     defaultValue: "Account",
            //     fieldType: "General",
            //     sortOrder: 2,
            // },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                // XNG-Change
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
